import { useCallback, useContext, useMemo, useState } from "react";
import { Button, ButtonGroup, ButtonProps } from "@mui/material";
import { MENU_FLOWS, DINING_MODES } from "@/constants/global";
import { LocalStorage } from "@/constants/localStorage";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { selectedBranch, setIsDineModeOpen } from "@/store/uiSlice";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import camelCase from "lodash/camelcase";
import get from "lodash/get";
import { checkServiceAvailability } from "@/utils/weekDays";
import { setOrdersFlow } from "@/features/order/slice";
import NotAvailableBranchDialog from "./components/NotAvailableBranchDialog";
import { RootState } from "@/store/store";

const DineModeTabs = () => {
  const dispatch = useAppDispatch();
  const { organization, branch } = useContext(WebsiteContext);
  const activeOrg = useAppSelector((store) => store.ui.activeOrganization);
  const org = useAppSelector((store) => store.organizations[activeOrg]);
  const selectedDineMode = useAppSelector((store) => store.order.flow);

  // si no hay activeBranch, buscarlo en localStorage
  const activeBranch = useAppSelector((state: RootState) => selectedBranch(state));
  const lsBranchId = localStorage.getItem(LocalStorage.BRANCH_ID);

  const branchToUse = useMemo(() => {
    if (!activeBranch && lsBranchId) {
      const orgBranch = branch?.find((b) => b.id === lsBranchId);
      return orgBranch;
    }
    return activeBranch;
  }, [activeBranch, lsBranchId, branch])

  const hasBranches = (branch ?? [])?.length > 0;

  const [requestedService, setRequestedService] = useState<MENU_FLOWS | null>(null);

  const handleClose = () => {
    dispatch(setIsDineModeOpen(false));
  };

  const handleModeSelection = useCallback((mode: MENU_FLOWS) => {
    dispatch(setOrdersFlow(mode));
    localStorage.setItem(LocalStorage.DINING_MODE, mode);
    handleClose();
  }, []);

  //detectar si hay branches
  const dineModes = useMemo(() => {
    return DINING_MODES
      .map((mode) => {

        // detectar branches correctamente
        const serviceConfig = get(
          organization || org,
          `settings.services.${camelCase(mode.id)}`
        );

        // NO hay branches → usar organización
        if (!hasBranches) {
          const isAvailable = checkServiceAvailability(serviceConfig);

          if (!isAvailable) return null;

          return {
            children: mode.title,
            variant: mode.id === selectedDineMode ? "contained" : "outlined",
            color: "secondary",
            key: mode.id,
            onClick: () => handleModeSelection(mode.id),
          } as ButtonProps;
        }

        // hay branches pero no hay branchToUse aún
        if (hasBranches && !branchToUse) {
          return {
            children: mode.title,
            variant: mode.id === selectedDineMode ? "contained" : "outlined",
            color: "secondary",
            key: mode.id,
          } as ButtonProps;
        }

        // usar sucursales
        const hasService = branchToUse?.services?.includes(mode.id);

        const isBranchOpen = checkServiceAvailability({
          isEnabled: true,
          schedule: branchToUse?.schedule,
        });

        const canUseService = hasService && isBranchOpen;

        if (!hasService) return null;

        return {
          children: mode.title,
          variant: mode.id === selectedDineMode ? "contained" : "outlined",
          color: "secondary",
          key: mode.id,

          onClick: () => {
            if (!canUseService) {
              setRequestedService(mode.id);
              return;
            }

            handleModeSelection(mode.id);
          },
        } as ButtonProps;
      })
      .filter(Boolean);
  }, [organization, org, selectedDineMode, branchToUse]);

  return (
    <>
      <ButtonGroup fullWidth size="small">
        {dineModes.map((mode) => (
          <Button {...mode} />
        ))}
      </ButtonGroup>

      <NotAvailableBranchDialog
        requestedService={requestedService}
        onClose={() => setRequestedService(null)}
      />

    </>
  );
};

export default DineModeTabs;